import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Card, CardContent, CardMedia, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import maleImg from '../../assets/male_modern.png';
import femaleImg from '../../assets/female_modern.png';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CakeIcon from '@mui/icons-material/Cake';
import WorkIcon from '@mui/icons-material/Work';
import { driveImageDownload } from '../../features/cartAction';

const UserCard = ({ user, isPaidUser }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loggedUser = useSelector((state) => state.auth.profileInfo?.user);
    const [fetchedImage, setFetchedImage] = useState(null);

    useEffect(() => {
        let isMounted = true;

        if (user?.userId) {
            dispatch(driveImageDownload(user.userId))
                .unwrap()
                .then((data) => {
                    if (isMounted && data && data.length > 0) {
                        const firstImageId = data[0].id;
                        setFetchedImage(`https://drive.google.com/thumbnail?id=${firstImageId}&sz=w1000`);
                    }
                })
                .catch((err) => {
                    console.error("Failed to load image for user", user.userId, err);
                });
        }

        return () => {
            isMounted = false;
        };
    }, [dispatch, user?.userId]);

    // Handle potential missing data gracefully AFTER hooks
    if (!user) return null;

    const handleViewProfile = () => {
        if (loggedUser) {
            navigate(`/moreDetailsPage/${loggedUser.id}/${user.userId}`);
        } else {
            navigate('/login');
        }
    };

    // Determine image source: 
    // 1. Fetched image from drive (highest priority)
    // 2. User object property (if available)
    // 3. Gender-based default (fallback)
    // const imageSrc = fetchedImage || user.images?.[0]?.url || user.image || user.profilePic || (user.gender?.toLowerCase() === 'female' ? femaleImg : maleImg);
    const imageSrc =  (user.gender?.toLowerCase() === 'female' ? femaleImg : maleImg);

    return (
        <Card
            sx={{
                maxWidth: 345,
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                bgcolor: 'background.paper',
                border: '1px solid rgba(0, 0, 0, 0.12)',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                    borderColor: 'primary.main',
                },
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #71123e, #C3937C)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease',
                    zIndex: 1,
                },
                '&:hover::before': {
                    opacity: 1,
                },
                m: 2
            }}
        >
            <Box sx={{ position: 'relative' }}>
                <CardMedia
                    component="img"
                    height="200"
                    image={imageSrc}
                    alt={`${user.firstName}`}
                    sx={{ objectFit: 'cover', objectPosition: 'top' }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%) rotate(-45deg)',
                        color: 'rgba(255, 255, 255, 0.3)',
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        pointerEvents: 'none',
                        userSelect: 'none',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                        zIndex: 1,
                        whiteSpace: 'nowrap'
                    }}
                >
                    Yuwathipathi
                </Box>
            </Box>
            <CardContent>
                <Typography gutterBottom variant="h6" component="div" noWrap>
                    {user.firstName}{" "}
                    {/* <span style={{ filter: isPaidUser ? 'none' : 'blur(5px)' }}>
                        {user.lastName}
                    </span> */}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                    <CakeIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2">{user.age} Years</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary' }}>
                    <WorkIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2" noWrap>{user.profession}</Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: 'text.secondary' }}>
                    <LocationOnIcon fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2" noWrap>{user.city}, {user.country}</Typography>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleViewProfile}
                    sx={{ borderRadius: 2 }}
                >
                    View Profile
                </Button>
            </CardContent>
        </Card>
    );
};

export default UserCard;
